import { useCallback, useEffect, useRef, useState } from 'react';
import {
  startSyncEngine,
  stopSyncEngine,
} from '../lib/syncEngine';
import { startLeaderElection, stopLeaderElection } from '../lib/queueLeader';
import { listConflicts, getCurrentScope } from '../lib/offlineQueueDB';

/**
 * A6 Sub-Phase 5 Block 5-MAIN-c — boots the offline sync engine and surfaces
 * Tier-3 concurrency conflicts to the UI.
 *
 * Without this hook the sync engine never runs in production (it was only
 * started in tests). Mounting this in App starts leader election + sync, and
 * routes Tier-3 conflicts (drift) to a user-facing drawer.
 *
 * @param {object} opts
 * @param {boolean} opts.enabled - only run when authenticated
 * @returns {{ activeConflict: object|null, dismissConflict: () => void, refreshConflicts: () => Promise<void> }}
 */
export function useOfflineSync({ enabled } = { enabled: true }) {
  const [activeConflict, setActiveConflict] = useState(null);
  const startedRef = useRef(false);

  const buildConflict = useCallback((entry, detail) => ({
    entryId: entry.id,
    conflicting_fields: detail.conflicting_fields || [],
    current_state: detail.current_state || null,
    client_state: detail.client_state || entry.body || null,
    client_base: detail.client_base || entry.originalState || null,
    drift_detected: !!detail.drift_detected,
    drift_seconds: detail.drift_seconds ?? null,
  }), []);

  const refreshConflicts = useCallback(async () => {
    const scope = getCurrentScope();
    if (!scope) return;
    try {
      const conflicts = await listConflicts(scope);
      const concurrency = conflicts.find(
        (c) => c.conflictDetail && c.conflictDetail.kind === 'concurrency',
      );
      if (concurrency) {
        setActiveConflict((prev) => prev || buildConflict(concurrency, concurrency.conflictDetail));
      } else {
        setActiveConflict(null);
      }
    } catch {
      /* store may be empty / unavailable — non-fatal */
    }
  }, [buildConflict]);

  const dismissConflict = useCallback(() => {
    setActiveConflict(null);
    refreshConflicts();
  }, [refreshConflicts]);

  useEffect(() => {
    if (!enabled) return undefined;
    if (startedRef.current) return undefined;
    startedRef.current = true;

    startLeaderElection();
    startSyncEngine({
      onEntryProcessed: (entry, result) => {
        if (
          result
          && result.category === 'conflict'
          && result.detail
          && result.detail.kind === 'concurrency'
        ) {
          setActiveConflict((prev) => prev || buildConflict(entry, result.detail));
        }
      },
    });

    refreshConflicts();

    return () => {
      stopSyncEngine();
      stopLeaderElection();
      startedRef.current = false;
    };
  }, [enabled, buildConflict, refreshConflicts]);

  return { activeConflict, dismissConflict, refreshConflicts };
}

export default useOfflineSync;
